import React, { useEffect, useState, useMemo } from 'react';
import { Table, Select, Input, Space, Tag, Button, Modal, Card, Row, Col, Divider, Image } from 'antd';
import { SearchOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, FileImageOutlined, DownloadOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import { toast } from 'sonner';
import { fetchRequestListApi, getRequestTypeDetail, requestAcceptRejectApi, fetchBankPanRecordsApi } from '../../../services/requestManagement';
import { dateFormat, timeFormat } from '../../../utils/dateFormat';
import profileImage from '../../../Assests/image/profile_not_found.png';
import RequestDetail from '../../requestManagement/RequestDetail';
import useFullPageLoader from '../../../hooks/useFullPageLoader';
import useDownloadWithPassword from '../../../hooks/useDownloadWithPassword';
import {
  getRequestKind,
  getRequestTypeBadgeColor,
  isBankPanRequestType,
  isBankRequestType,
  isEditAccessRequest,
  isPanRequestType,
  parseRequestJson,
  resolveBankImageUrl,
  resolvePanImageUrl,
} from '../../../utils/requestChangeControl';

const { Option } = Select;

const BankPanManagement = ({ canViewRequest = true, canViewRecord = true, requestsOnly = false }) => {
  const [loading, setLoading] = useState(false);
  const [requestList, setRequestList] = useState([]);
  const [allRequests, setAllRequests] = useState([]); // Store all requests for stats
  const [liveRecords, setLiveRecords] = useState([]); // Live Bank/PAN documents for Record tab
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const { requestDownloadWithCallback, DownloadPasswordModal } = useDownloadWithPassword();

  // Tab state
  const [activeTab, setActiveTab] = useState(
    requestsOnly || canViewRequest ? 'request' : 'final'
  ); // 'request' or 'final'

  // Pagination state for Final/Record tab
  const [finalPagination, setFinalPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Filter states for Request tab
  const [filters, setFilters] = useState({
    search: '',
    bankName: [], // Array for multiple bank selection
    requestType: '', // 'BANK' or 'PANCARD' or '' for all
    approveStatus: '', // 1=pending, 2=approved, 3=rejected
    imageFilter: '', // 'pan', 'bank', 'hasImage', 'noImage'
  });

  // Filter states for Final tab
  const [finalFilters, setFinalFilters] = useState({
    search: '',
    bankName: [], // Array for multiple bank selection
    panStatus: '', // 'approved', 'notApproved'
    bankStatus: '', // 'approved', 'notApproved'
    imageFilter: '', // 'pan', 'bank', 'both', 'none'
  });

  // Common bank shortcuts shown on top of dropdowns
  const commonBankNames = [
    'SBI',
    'HDFC',
    'ICICI',
    'AXIS',
    'IDFC',
    'PNB',
    'BOB',
    'KOTAK',
    'YES BANK',
    'CANARA',
    'UNION BANK',
  ];

  // Bank names to exclude from dropdown
  const excludedBankNames = ['Gurgaon sec 14', 'Gautam', 'tst bank nam e', 'tst bank name','test'];

  // Bank name mapping: abbreviations to full names (for filtering)
  const bankNameMapping = {
    'sbi': ['state bank of india', 'sbi'],
    'hdfc': ['hdfc bank', 'hdfc'],
    'icici': ['icici bank', 'icici'],
    'axis': ['axis bank', 'axis'],
    'idfc': ['idfc first bank', 'idfc bank', 'idfc'],
    'pnb': ['punjab national bank', 'pnb'],
    'bob': ['bank of baroda', 'bob'],
    'kotak': ['kotak mahindra bank', 'kotak'],
    'yes bank': ['yes bank'],
    'canara': ['canara bank', 'canara'],
    'union bank': ['union bank of india', 'union bank'],
  };

  // Helper function to check if bank name matches (considering abbreviations and full names)
  const matchesBankName = (selectedBank, actualBankName) => {
    if (!selectedBank || !actualBankName) return false;
    const selectedLower = selectedBank.toLowerCase().trim();
    const actualLower = actualBankName.toLowerCase().trim();
    
    // Direct substring match (e.g., "SBI" in "SBI Bank" or "State Bank" in "State Bank of India")
    if (actualLower.includes(selectedLower) || selectedLower.includes(actualLower)) {
      return true;
    }
    
    // Check mapping for abbreviation/full name match
    // Find if selected bank matches any variation in the mapping
    for (const [key, variations] of Object.entries(bankNameMapping)) {
      const selectedMatches = variations.some(v => selectedLower.includes(v) || v.includes(selectedLower));
      const actualMatches = variations.some(v => actualLower.includes(v) || v.includes(actualLower));
      
      // If both selected and actual match variations from the same bank group, they match
      if (selectedMatches && actualMatches) {
        return true;
      }
    }
    
    return false;
  };

  const normalizeSearchValue = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).toLowerCase();
  };

  const matchesSearchValue = (value, searchLower) => {
    return normalizeSearchValue(value).includes(searchLower);
  };

  const getRequestConsultantObjectId = (record) => {
    return record?.consultantId?._id || record?.consultantObjectId || record?.consultantId || '---';
  };

  const getRequestUserId = (record) => {
    return record?.userId || record?.consultantId?.userId || '---';
  };

  const getRequestImageUrl = (record) => {
    try {
      const newData = parseRequestJson(record.newData) || {};
      const currentData = parseRequestJson(record.currentData) || {};
      const type = record.requestType;

      if (isBankRequestType(type)) {
        return resolveBankImageUrl(type === 'BANK_EDIT' ? currentData : newData) ||
          resolveBankImageUrl(currentData) || '';
      }

      if (isPanRequestType(type)) {
        return resolvePanImageUrl(type === 'PAN_EDIT' ? currentData : newData) ||
          resolvePanImageUrl(currentData) || '';
      }

      return '';
    } catch {
      return '';
    }
  };

  const getExcelHyperlinkCell = (url, label = 'View Image') => {
    const cleanUrl = String(url || '').trim();
    if (!cleanUrl || cleanUrl === '---') return '---';
    return `=HYPERLINK("${cleanUrl.replace(/"/g, '""')}","${label.replace(/"/g, '""')}")`;
  };

  // Bank name options (for dropdown) - derived from all fetched requests
  const bankNameOptions = useMemo(() => {
    const names = new Set();
    allRequests.forEach(req => {
      try {
        const newData = req.newData ? (typeof req.newData === 'string' ? JSON.parse(req.newData) : req.newData) : {};
        const currentData = req.currentData ? (typeof req.currentData === 'string' ? JSON.parse(req.currentData) : req.currentData) : {};
        const bankName = newData.bankName || currentData.bankName;
        if (bankName && !excludedBankNames.includes(bankName)) {
          names.add(bankName);
        }
      } catch {
        // ignore parse errors
      }
    });
    return Array.from(names);
  }, [allRequests]);

  const bankNameOptionsDisplay = useMemo(() => {
    const dynamic = bankNameOptions.filter(name => !commonBankNames.includes(name));
    return {
      common: commonBankNames,
      dynamic,
    };
  }, [bankNameOptions, commonBankNames]);

  // Modal states
  const [viewModal, setViewModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newData, setNewData] = useState({});
  const [currentData, setCurrentData] = useState({});
  const [requestId, setRequestId] = useState({});
  const [requestdetails, setRequestdetails] = useState(null);
  
  // Image preview modal state
  const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [imageRotation, setImageRotation] = useState(0);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let allRequests = [];
      let totalCount = 0;

      const fetchByType = async (requestType) => {
        const sendData = {
          page: 1,
          limit: 1000,
          requestType,
        };
        if (filters.approveStatus) {
          sendData.approveStatus = filters.approveStatus;
        }
        if (filters.search) {
          sendData.search = filters.search;
        }
        const { data } = await fetchRequestListApi(sendData);
        if (data?.code !== 200) return [];
        return (data?.result?.reqList || []).filter((req) => isBankPanRequestType(req.requestType));
      };

      const typesToFetch = filters.requestType
        ? [filters.requestType]
        : ['BANK', 'BANK_EDIT', 'PAN', 'PAN_EDIT'];

      const lists = await Promise.all(typesToFetch.map(fetchByType));
      const byId = new Map();
      lists.flat().forEach((req) => {
        if (req?._id) byId.set(String(req._id), req);
      });
      allRequests = [...byId.values()];
      totalCount = allRequests.length;

      // Apply search filter on frontend if needed (for better UX)
      if (filters.search && allRequests.length > 0) {
        const searchLower = filters.search.toLowerCase().trim();
        allRequests = allRequests.filter(req => {
          const matchesName =
            matchesSearchValue(req.consName, searchLower) ||
            matchesSearchValue(req.accountName, searchLower) ||
            matchesSearchValue(req.userName, searchLower) ||
            matchesSearchValue(req.userId, searchLower) ||
            matchesSearchValue(req.consultantId?._id || req.consultantId, searchLower) ||
            matchesSearchValue(req._id, searchLower) ||
            matchesSearchValue(req.mobile, searchLower) ||
            matchesSearchValue(req.phone, searchLower) ||
            matchesSearchValue(req.email, searchLower);

          // For bank related fields, inspect newData/currentData
          let matchesDetails = false;
          try {
            const newData = req.newData ? (typeof req.newData === 'string' ? JSON.parse(req.newData) : req.newData) : {};
            const currentData = req.currentData ? (typeof req.currentData === 'string' ? JSON.parse(req.currentData) : req.currentData) : {};
            const panNumber = newData.panNumber || currentData.panNumber;
            const bankHolder = newData.bankHolderName || currentData.bankHolderName;
            const bankName = newData.bankName || currentData.bankName;
            const ifsc = newData.ifsc || currentData.ifsc;
            const accountNumber = newData.accountNumber || currentData.accountNumber;

            matchesDetails =
              matchesSearchValue(panNumber, searchLower) ||
              matchesSearchValue(bankHolder, searchLower) ||
              matchesSearchValue(bankName, searchLower) ||
              matchesSearchValue(ifsc, searchLower) ||
              matchesSearchValue(accountNumber, searchLower);
          } catch {
            matchesDetails = false;
          }

          return matchesName || matchesDetails;
        });
      }

      // Apply bank name filter (explicit) - supports multiple selections
      if (filters.bankName && Array.isArray(filters.bankName) && filters.bankName.length > 0) {
        const selectedBanks = filters.bankName.filter(b => b && typeof b === 'string');
        if (selectedBanks.length > 0) {
          allRequests = allRequests.filter(req => {
            try {
              const newData = req.newData ? (typeof req.newData === 'string' ? JSON.parse(req.newData) : req.newData) : {};
              const currentData = req.currentData ? (typeof req.currentData === 'string' ? JSON.parse(req.currentData) : req.currentData) : {};
              const bankName = newData.bankName || currentData.bankName;
              if (!bankName || typeof bankName !== 'string') return false;
              return selectedBanks.some(selectedBank => matchesBankName(selectedBank, bankName));
            } catch {
              return false;
            }
          });
        }
      }

      // Sort by createdAt descending
      allRequests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      // Store all requests for stats calculation
      setAllRequests(allRequests);

      // Apply pagination
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const paginatedRequests = allRequests.slice(startIndex, endIndex);

      setRequestList(paginatedRequests);
      setPagination(prev => ({
        ...prev,
        total: allRequests.length,
      }));
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to fetch requests');
      setRequestList([]);
      setAllRequests([]);
      setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const fetchBankPanRecords = async () => {
    if (!canViewRecord) return;
    try {
      const { data } = await fetchBankPanRecordsApi();
      if (data?.code === 200 && Array.isArray(data.result)) {
        setLiveRecords(data.result);
      }
    } catch (error) {
      console.error('Error fetching bank/PAN records:', error);
    }
  };

  const refreshBankPanViews = () => {
    fetchRequests();
    fetchBankPanRecords();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFinalFilterChange = (key, value) => {
    setFinalFilters(prev => ({ ...prev, [key]: value }));
    setFinalPagination(prev => ({ ...prev, current: 1 })); // Reset to first page when filter changes
  };

  const handleTableChange = (paginationInfo) => {
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };

  // Helper function to extract cell value for export
  const getExportCellValue = (record, colKey) => {
    try {
      if (colKey === 'index') {
        return allRequests.indexOf(record) + 1;
      } else if (colKey === 'profileImage') {
        return record.profileImage ? 'Yes' : 'No';
      } else if (colKey === 'consultantObjectId') {
        return getRequestConsultantObjectId(record);
      } else if (colKey === 'userId') {
        return getRequestUserId(record);
      } else if (colKey === 'consName') {
        return record.consName || '---';
      } else if (colKey === 'requestType') {
        const kind = getRequestKind(record);
        return kind.kindLabel ? `${kind.typeLabel} (${kind.kindLabel})` : (kind.typeLabel || '---');
      } else if (colKey === 'approveStatus') {
        return getStatusLabel(record.approveStatus);
      } else if (colKey === 'pan') {
        const newData = record.newData ? (typeof record.newData === 'string' ? JSON.parse(record.newData) : record.newData) : {};
        const currentData = record.currentData ? (typeof record.currentData === 'string' ? JSON.parse(record.currentData) : record.currentData) : {};
        if (record.approveStatus === 2) {
          return newData.panNumber || '---';
        } else {
          return newData.panNumber || currentData.panNumber || '---';
        }
      } else if (colKey === 'bankName') {
        const newData = record.newData ? (typeof record.newData === 'string' ? JSON.parse(record.newData) : record.newData) : {};
        const currentData = record.currentData ? (typeof record.currentData === 'string' ? JSON.parse(record.currentData) : record.currentData) : {};
        if (isBankRequestType(record.requestType)) {
          if (record.approveStatus === 2) {
            return newData.bankName || '---';
          } else {
            return newData.bankName || currentData.bankName || '---';
          }
        } else {
          return '---';
        }
      } else if (colKey === 'ifsc') {
        const newData = record.newData ? (typeof record.newData === 'string' ? JSON.parse(record.newData) : record.newData) : {};
        const currentData = record.currentData ? (typeof record.currentData === 'string' ? JSON.parse(record.currentData) : record.currentData) : {};
        if (isBankRequestType(record.requestType)) {
          if (record.approveStatus === 2) {
            return newData.ifsc || '---';
          } else {
            return newData.ifsc || currentData.ifsc || '---';
          }
        } else {
          return '---';
        }
      } else if (colKey === 'accountHolderName') {
        const newData = record.newData ? (typeof record.newData === 'string' ? JSON.parse(record.newData) : record.newData) : {};
        const currentData = record.currentData ? (typeof record.currentData === 'string' ? JSON.parse(record.currentData) : record.currentData) : {};
        if (isBankRequestType(record.requestType)) {
          if (record.approveStatus === 2) {
            return newData.bankHolderName || '---';
          } else {
            return newData.bankHolderName || currentData.bankHolderName || '---';
          }
        } else {
          return '---';
        }
      } else if (colKey === 'accountNumber') {
        const newData = record.newData ? (typeof record.newData === 'string' ? JSON.parse(record.newData) : record.newData) : {};
        const currentData = record.currentData ? (typeof record.currentData === 'string' ? JSON.parse(record.currentData) : record.currentData) : {};
        if (isBankRequestType(record.requestType)) {
          if (record.approveStatus === 2) {
            return newData.accountNumber || '---';
          } else {
            return newData.accountNumber || currentData.accountNumber || '---';
          }
        } else {
          return '---';
        }
      } else if (colKey === 'image') {
        return getExcelHyperlinkCell(getRequestImageUrl(record));
      } else if (colKey === 'requestTime') {
        return record.createdAt ? `${dateFormat(record.createdAt)} ${timeFormat(record.createdAt)}` : '---';
      } else if (colKey === 'approveTime') {
        if (record.approveStatus === 2 || record.approveStatus === 3) {
          return record.updatedAt ? `${dateFormat(record.updatedAt)} ${timeFormat(record.updatedAt)}` : '---';
        }
        return '---';
      } else {
        return '---';
      }
    } catch {
      return '---';
    }
  };

  // Helper function to extract cell value for final records export
  const getFinalExportCellValue = (record, colKey) => {
    try {
      if (colKey === 'index') {
        return finalRecords.indexOf(record) + 1;
      } else if (colKey === 'profileImage') {
        return record.profileImage ? 'Yes' : 'No';
      } else if (colKey === 'consultantObjectId') {
        return record.consultantObjectId || record.consultantId || '---';
      } else if (colKey === 'userId') {
        return record.userId || '---';
      } else if (colKey === 'consName') {
        return record.consName || '---';
      } else if (colKey === 'panNumber') {
        return record.panNumber || '---';
      } else if (colKey === 'panStatus') {
        return record.panStatus || '---';
      } else if (colKey === 'bankName') {
        return record.bankName || '---';
      } else if (colKey === 'ifsc') {
        return record.ifsc || '---';
      } else if (colKey === 'accountHolderName') {
        return record.accountHolderName || '---';
      } else if (colKey === 'accountNumber') {
        return record.accountNumber || '---';
      } else if (colKey === 'bankStatus') {
        return record.bankStatus || '---';
      } else if (colKey === 'panImage') {
        return getExcelHyperlinkCell(record.panImage, 'View PAN Image');
      } else if (colKey === 'bankImage') {
        return getExcelHyperlinkCell(record.bankImage, 'View Bank Image');
      } else {
        return '---';
      }
    } catch {
      return '---';
    }
  };

  // Export to Excel (CSV format) for Request tab
  const exportToExcel = () => {
    try {
      // Get visible columns based on current filter
      const visibleColumns = columns.filter(col => col.key !== 'action');

      // Create CSV headers
      const headers = visibleColumns.map(col => col.title).join(',');
      
      // Create CSV rows
      const rows = allRequests.map(record => {
        return visibleColumns.map(col => {
          let value = getExportCellValue(record, col.key);
          // Escape commas and quotes in CSV
          value = String(value).replace(/"/g, '""');
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            value = `"${value}"`;
          }
          return value;
        }).join(',');
      });

      // Combine headers and rows
      const csvContent = [headers, ...rows].join('\n');
      
      // Create blob and download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `bank_pan_management_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  };

  // Export to Excel for Final tab
  const exportFinalToExcel = () => {
    try {
      // Get visible columns for final records
      const visibleColumns = finalRecordsColumns;

      // Create CSV headers
      const headers = visibleColumns.map(col => col.title).join(',');
      
      // Create CSV rows
      const rows = finalRecords.map(record => {
        return visibleColumns.map(col => {
          let value = getFinalExportCellValue(record, col.key);
          // Escape commas and quotes in CSV
          value = String(value).replace(/"/g, '""');
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            value = `"${value}"`;
          }
          return value;
        }).join(',');
      });

      // Combine headers and rows
      const csvContent = [headers, ...rows].join('\n');
      
      // Create blob and download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `final_bank_pan_records_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      // Check if libraries are available, if not use fallback
      if (typeof window !== 'undefined' && window.jsPDF) {
        // Libraries are loaded globally
        const { jsPDF } = window;
        const doc = new jsPDF('landscape');
        
        // Get visible columns based on current filter
        const visibleColumns = columns.filter(col => 
          col.key !== 'action' && col.key !== 'image'
        );

        // Prepare table data
        const tableData = allRequests.map(record => {
          return visibleColumns.map(col => getExportCellValue(record, col.key));
        });

        // Add title
        doc.setFontSize(16);
        doc.text('Bank & PAN Management Report', 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);
        doc.text(`Total Records: ${allRequests.length}`, 14, 27);

        // Add table using autoTable if available
        if (window.jspdf && window.jspdf.autoTable) {
          window.jspdf.autoTable(doc, {
            head: [visibleColumns.map(col => col.title)],
            body: tableData,
            startY: 32,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [66, 139, 202] },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            margin: { top: 32 },
          });
        } else {
          // Fallback: simple table without autoTable
          let yPos = 32;
          doc.setFontSize(10);
          visibleColumns.forEach((col, idx) => {
            doc.text(col.title, 14 + (idx * 40), yPos);
          });
          yPos += 10;
          tableData.forEach(row => {
            row.forEach((cell, idx) => {
              doc.text(String(cell), 14 + (idx * 40), yPos);
            });
            yPos += 7;
            if (yPos > 200) {
              doc.addPage();
              yPos = 20;
            }
          });
        }

        // Save PDF
        doc.save(`bank_pan_management_${new Date().toISOString().split('T')[0]}.pdf`);
        toast.success('PDF file downloaded successfully');
      } else {
        // Fallback: Use browser print functionality
        const printWindow = window.open('', '_blank');
        const visibleColumns = columns.filter(col => 
          col.key !== 'action' && col.key !== 'image'
        );
        
        let html = `
          <html>
            <head>
              <title>Bank & PAN Management Report</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                h1 { color: #333; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #428bca; color: white; }
                tr:nth-child(even) { background-color: #f2f2f2; }
              </style>
            </head>
            <body>
              <h1>Bank & PAN Management Report</h1>
              <p>Generated on: ${new Date().toLocaleString()}</p>
              <p>Total Records: ${allRequests.length}</p>
              <table>
                <thead>
                  <tr>
                    ${visibleColumns.map(col => `<th>${col.title}</th>`).join('')}
                  </tr>
                </thead>
                <tbody>
        `;
        
        allRequests.forEach(record => {
          html += '<tr>';
          visibleColumns.forEach(col => {
            const value = getExportCellValue(record, col.key);
            html += `<td>${value}</td>`;
          });
          html += '</tr>';
        });
        
        html += `
                </tbody>
              </table>
            </body>
          </html>
        `;
        
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
        toast.info('Opening print dialog. You can save as PDF from there.');
      }
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export to PDF. Please install jspdf and jspdf-autotable for better PDF export.');
    }
  };

  // Export to PDF for Final tab
  const exportFinalToPDF = () => {
    try {
      // Check if libraries are available, if not use fallback
      if (typeof window !== 'undefined' && window.jsPDF) {
        // Libraries are loaded globally
        const { jsPDF } = window;
        const doc = new jsPDF('landscape');
        
        // Get visible columns for final records
        const visibleColumns = finalRecordsColumns.filter(col => 
          col.key !== 'panImage' && col.key !== 'bankImage'
        );

        // Prepare table data
        const tableData = finalRecords.map(record => {
          return visibleColumns.map(col => getFinalExportCellValue(record, col.key));
        });

        // Add title
        doc.setFontSize(16);
        doc.text('Final Bank & PAN Records Report', 14, 15);
        doc.setFontSize(10);
        doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);
        doc.text(`Total Records: ${finalRecords.length}`, 14, 27);

        // Add table using autoTable if available
        if (window.jspdf && window.jspdf.autoTable) {
          window.jspdf.autoTable(doc, {
            head: [visibleColumns.map(col => col.title)],
            body: tableData,
            startY: 32,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [66, 139, 202] },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            margin: { top: 32 },
          });
        } else {
          // Fallback: simple table without autoTable
          let yPos = 32;
          doc.setFontSize(10);
          visibleColumns.forEach((col, idx) => {
            doc.text(col.title, 14 + (idx * 40), yPos);
          });
          yPos += 10;
          tableData.forEach(row => {
            row.forEach((cell, idx) => {
              doc.text(String(cell), 14 + (idx * 40), yPos);
            });
            yPos += 7;
            if (yPos > 200) {
              doc.addPage();
              yPos = 20;
            }
          });
        }

        // Save PDF
        doc.save(`final_bank_pan_records_${new Date().toISOString().split('T')[0]}.pdf`);
        toast.success('PDF file downloaded successfully');
      } else {
        // Fallback: Use browser print functionality
        const printWindow = window.open('', '_blank');
        const visibleColumns = finalRecordsColumns.filter(col => 
          col.key !== 'panImage' && col.key !== 'bankImage'
        );
        
        let html = `
          <html>
            <head>
              <title>Final Bank & PAN Records Report</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                h1 { color: #333; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #428bca; color: white; }
                tr:nth-child(even) { background-color: #f2f2f2; }
              </style>
            </head>
            <body>
              <h1>Final Bank & PAN Records Report</h1>
              <p>Generated on: ${new Date().toLocaleString()}</p>
              <p>Total Records: ${finalRecords.length}</p>
              <table>
                <thead>
                  <tr>
                    ${visibleColumns.map(col => `<th>${col.title}</th>`).join('')}
                  </tr>
                </thead>
                <tbody>
        `;
        
        finalRecords.forEach(record => {
          html += '<tr>';
          visibleColumns.forEach(col => {
            const value = getFinalExportCellValue(record, col.key);
            html += `<td>${value}</td>`;
          });
          html += '</tr>';
        });
        
        html += `
                </tbody>
              </table>
            </body>
          </html>
        `;
        
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
        toast.info('Opening print dialog. You can save as PDF from there.');
      }
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error('Failed to export to PDF. Please install jspdf and jspdf-autotable for better PDF export.');
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setRequestId({
      requestId: request?._id,
      approveStatus: request.approveStatus,
    });

    // Always edit the latest request payload (newData). Do not swap in
    // stale currentData for approved rows — that was overwriting live Bank/PAN.
    setNewData({
      ...(request?.newData || {}),
      requestType: request?.requestType,
    });
    setCurrentData({
      ...((
        !isEditAccessRequest(request?.requestType) && request.approveStatus === 2
          ? (request?.currentData || request?.newData)
          : request?.currentData
      ) || {}),
      consName: request?.consName,
      userName: request?.userName,
      requestType: request?.requestType,
      flagReason: request?.flagReason
    });
    setRequestdetails(request);
    setViewModal(true);
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 1:
        return <Tag icon={<ClockCircleOutlined />} color="warning">Pending</Tag>;
      case 2:
        return <Tag icon={<CheckCircleOutlined />} color="success">Approved</Tag>;
      case 3:
        return <Tag icon={<CloseCircleOutlined />} color="error">Rejected</Tag>;
      default:
        return <Tag>Unknown</Tag>;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1:
        return 'Pending';
      case 2:
        return 'Approved';
      case 3:
        return 'Rejected';
      default:
        return 'Unknown';
    }
  };

  useEffect(() => {
    if (requestsOnly) {
      setActiveTab('request');
    }
  }, [requestsOnly]);

  useEffect(() => {
    fetchRequests();
  }, [pagination.current, pagination.pageSize, filters]);

  useEffect(() => {
    if (canViewRecord) {
      fetchBankPanRecords();
    }
  }, [activeTab, canViewRecord]);

  useEffect(() => {
    if (activeTab === 'request' && !canViewRequest && canViewRecord) {
      setActiveTab('final');
    } else if (activeTab === 'final' && !canViewRecord && canViewRequest) {
      setActiveTab('request');
    }
  }, [activeTab, canViewRequest, canViewRecord]);

  const requestDetail = async () => {
    if (!requestId.requestId) return;
    try {
      showLoader();
      const { data } = await getRequestTypeDetail({
        requestId: requestId.requestId,
      });
      if (data.code === 200 && data.result) {
        const res = data.result;
        const reqType = res.requestType || requestdetails?.requestType;
        const editAccess = isEditAccessRequest(reqType);
        setNewData((prev) => ({
          ...(res.newData || prev || {}),
          requestType: prev?.requestType || reqType,
        }));
        setCurrentData((prev) => ({
          ...((editAccess ? res.currentData : (res.currentData || res.newData)) || prev || {}),
          consName: prev?.consName,
          userName: prev?.userName,
          requestType: prev?.requestType || reqType,
          flagReason: prev?.flagReason,
        }));
        setRequestdetails((prev) => ({
          ...prev,
          source: res.source || prev?.source,
          review: res.review || prev?.review,
        }));
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    if (viewModal && requestId.requestId) {
      requestDetail();
    }
  }, [viewModal]);

  const allColumns = [
    {
      title: 'S. No.',
      key: 'index',
      width: 80,
      render: (_, __, index) => {
        return (pagination.current - 1) * pagination.pageSize + index + 1;
      },
    },
    {
      title: 'Profile',
      dataIndex: 'profileImage',
      key: 'profileImage',
      width: 100,
      render: (image, record) => (
        <img
          src={image || profileImage}
          alt={record.consName || 'Profile'}
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
      ),
    },
    {
      title: 'Consultant Name',
      dataIndex: 'consName',
      key: 'consName',
      width: 150,
      ellipsis: true,
      render: (name) => name || '---',
    },
    {
      title: 'Consultant _id',
      key: 'consultantObjectId',
      width: 230,
      ellipsis: true,
      render: (_, record) => getRequestConsultantObjectId(record),
    },
    {
      title: 'User ID',
      key: 'userId',
      width: 140,
      ellipsis: true,
      render: (_, record) => getRequestUserId(record),
    },
    {
      title: 'Request Type',
      dataIndex: 'requestType',
      key: 'requestType',
      width: 180,
      render: (type, record) => {
        const kind = getRequestKind(record);
        return (
          <div>
            <Tag color={getRequestTypeBadgeColor(type)}>{kind.typeLabel}</Tag>
            {kind.kindLabel ? (
              <Tag color={kind.kind === 'access' ? 'red' : kind.kind === 'admin' ? 'geekblue' : kind.kind === 'update' ? 'cyan' : 'green'}>
                {kind.kindLabel}
              </Tag>
            ) : null}
          </div>
        );
      },
    },
    {
      title: 'Request Time',
      key: 'requestTime',
      width: 180,
      render: (_, record) => {
        return record.createdAt ? dateFormat(record.createdAt) : '---';
      },
    },
    {
      title: 'Approve Time',
      key: 'approveTime',
      width: 180,
      render: (_, record) => {
        // Show updatedAt if status is approved or not approved, and updatedAt exists and is different from createdAt
        if ((record.approveStatus === 2 || record.approveStatus === 3) && record.updatedAt) {
          // Only show if updatedAt is different from createdAt (meaning it was actually updated)
          const createdAt = new Date(record.createdAt).getTime();
          const updatedAt = new Date(record.updatedAt).getTime();
          if (updatedAt > createdAt) {
            return dateFormat(record.updatedAt);
          }
        }
        return '---';
      },
    },
    {
        title: 'Status',
        dataIndex: 'approveStatus',
        key: 'approveStatus',
        width: 120,
        render: (status) => getStatusTag(status),
        filters: [
          { text: 'Pending', value: 1 },
          { text: 'Approved', value: 2 },
          { text: 'Rejected', value: 3 },
        ],
        onFilter: (value, record) => record.approveStatus === value,
      },
    {
      title: 'PAN',
      key: 'pan',
      width: 150,
      render: (_, record) => {
        try {
          const newData = parseRequestJson(record.newData) || {};
          const currentData = parseRequestJson(record.currentData) || {};
          return newData.panNumber || currentData.panNumber || '---';
        } catch {
          return '---';
        }
      },
    },
    {
      title: 'Bank  Name',
      key: 'bankName',
      width: 200,
      render: (_, record) => {
        try {
          const newData = parseRequestJson(record.newData) || {};
          const currentData = parseRequestJson(record.currentData) || {};
          if (isBankRequestType(record.requestType)) {
            return newData.bankName || currentData.bankName || '---';
          }
          return '---';
        } catch {
          return '---';
        }
      },
    },
    {
      title: 'Bank IFSC code',
      key: 'ifsc',
      width: 150,
      render: (_, record) => {
        try {
          const newData = parseRequestJson(record.newData) || {};
          const currentData = parseRequestJson(record.currentData) || {};
          if (isBankRequestType(record.requestType)) {
            return newData.ifsc || currentData.ifsc || '---';
          }
          return '---';
        } catch {
          return '---';
        }
      },
    },
    {
      title: 'Account Holder Name',
      key: 'accountHolderName',
      width: 180,
      render: (_, record) => {
        try {
          const newData = parseRequestJson(record.newData) || {};
          const currentData = parseRequestJson(record.currentData) || {};
          if (isBankRequestType(record.requestType)) {
            return newData.bankHolderName || currentData.bankHolderName || '---';
          }
          return '---';
        } catch {
          return '---';
        }
      },
    },
    {
      title: 'Account Number',
      key: 'accountNumber',
      width: 150,
      render: (_, record) => {
        try {
          const newData = parseRequestJson(record.newData) || {};
          const currentData = parseRequestJson(record.currentData) || {};
          if (isBankRequestType(record.requestType)) {
            return newData.accountNumber || currentData.accountNumber || '---';
          }
          return '---';
        } catch {
          return '---';
        }
      },
    },
    {
      title: 'Image',
      key: 'image',
      width: 100,
      align: 'center',
      render: (_, record) => {
        try {
          const imageUrl = getRequestImageUrl(record);
          const imageTitle = isPanRequestType(record.requestType) ? 'PAN Card' : 'Bank Passbook';
          
          if (isBankPanRequestType(record.requestType) && imageUrl) {
            return (
              <Button
                type="link"
                icon={<FileImageOutlined />}
                onClick={() => {
                  setPreviewImage(imageUrl);
                  setPreviewTitle(`${imageTitle} - ${record.consName || 'Consultant'}`);
                  // Auto-detect if it's a PAN card (usually needs rotation) or bank passbook
                  if (isPanRequestType(record.requestType)) {
                    setImageRotation(-90); // Rotate PAN cards -90 degrees to correct orientation
                  } else {
                    setImageRotation(0); // Bank passbooks usually don't need rotation
                  }
                  setImagePreviewVisible(true);
                }}
              >
                View
              </Button>
            );
          }
          return '---';
        } catch {
          return '---';
        }
      },
    },
   
    {
      title: 'Action',
      key: 'action',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
        >
          View
        </Button>
      ),
    },
  ];

  // Filter columns based on selected request type
  const columns = useMemo(() => {
    if (filters.requestType === 'BANK' || filters.requestType === 'BANK_EDIT') {
      return allColumns.filter(col => col.key !== 'pan');
    } else if (filters.requestType === 'PAN' || filters.requestType === 'PAN_EDIT') {
      // Hide bank-related columns when PAN Card is selected
      return allColumns.filter(col => 
        col.key !== 'bankName' && 
        col.key !== 'ifsc' && 
        col.key !== 'accountHolderName' && 
        col.key !== 'accountNumber'
      );
    }
    // Show all columns when no filter or "All Types" is selected
    return allColumns;
  }, [filters.requestType, pagination.current, pagination.pageSize]);

  // Apply image filter to all requests (for pagination total)
  const filteredAllRequests = useMemo(() => {
    let filtered = allRequests;

    if (filters.imageFilter) {
      filtered = filtered.filter(record => {
        try {
          const newData = record.newData ? (typeof record.newData === 'string' ? JSON.parse(record.newData) : record.newData) : {};
          const currentData = record.currentData ? (typeof record.currentData === 'string' ? JSON.parse(record.currentData) : record.currentData) : {};
          
          const hasBankImage = newData.attachment || newData.attachmentUrl || newData.bankAttachment || currentData.attachment || currentData.attachmentUrl || currentData.bankAttachment;
          const isCancelOrCheck = record.approveStatus === 1 || record.approveStatus === 3;

          if (filters.imageFilter === 'bankPassbook') {
            return hasBankImage && !isCancelOrCheck;
          } else if (filters.imageFilter === 'cancelCheck') {
            return isCancelOrCheck && !hasBankImage;
          }
          return true;
        } catch {
          return true;
        }
      });
    }

    return filtered;
  }, [allRequests, filters.imageFilter]);

  // Filter request list based on image filter
  const filteredRequestList = useMemo(() => {
    let filtered = requestList;

    if (filters.imageFilter) {
      filtered = filtered.filter(record => {
        try {
          const newData = record.newData ? (typeof record.newData === 'string' ? JSON.parse(record.newData) : record.newData) : {};
          const currentData = record.currentData ? (typeof record.currentData === 'string' ? JSON.parse(record.currentData) : record.currentData) : {};
          
          const hasBankImage = newData.attachment || newData.attachmentUrl || newData.bankAttachment || currentData.attachment || currentData.attachmentUrl || currentData.bankAttachment;
          const isCancelOrCheck = record.approveStatus === 1 || record.approveStatus === 3;

          if (filters.imageFilter === 'bankPassbook') {
            // Bank Passbook: Show records with bank passbook images, but exclude cancelled/check records
            return hasBankImage && !isCancelOrCheck;
          } else if (filters.imageFilter === 'cancelCheck') {
            // Cancel Check Image: Show records that are cancelled/need checking, but exclude those with bank passbook images
            return isCancelOrCheck && !hasBankImage;
          }
          return true;
        } catch {
          return true;
        }
      });
    }

    return filtered;
  }, [requestList, filters.imageFilter]);

  // Get statistics from all requests, not just current page
  const stats = {
    total: allRequests.length,
    pending: allRequests.filter(r => r.approveStatus === 1).length,
    approved: allRequests.filter(r => r.approveStatus === 2).length,
    notApproved: allRequests.filter(r => r.approveStatus === 3).length,
  };

  // Process final records: Combine PAN and Bank details for all consultants (including not approved)
  // Process final records: live Bank/PAN docs first, otherwise combine requests
  // without letting an older pending/rejected request hide an approved update.
  const finalRecords = useMemo(() => {
    const consultantMap = new Map();
    const applyFromRequests = (requests) => {
      const ranked = [...requests].sort((a, b) => {
        const rank = (status) => (status === 2 ? 0 : status === 1 ? 1 : 2);
        const byStatus = rank(a.approveStatus) - rank(b.approveStatus);
        if (byStatus !== 0) return byStatus;
        return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
      });

      ranked.forEach(req => {
        const consultantId = req.consultantId?._id || req.consultantId;
        if (!consultantId) return;
        const idKey = String(consultantId);

        if (!consultantMap.has(idKey)) {
          consultantMap.set(idKey, {
            consultantId: idKey,
            consultantObjectId: idKey,
            requestId: req._id || null,
            consName: req.consName || '---',
            accountName: req.accountName || req.consultantId?.accountName || null,
            userName: req.userName || req.consultantId?.userName || req.consultantId?.username || null,
            userId: req.userId || req.consultantId?.userId || null,
            email: req.email || req.consultantId?.email || null,
            contactNumber: req.phone || req.mobile || req.consultantId?.phone || req.consultantId?.mobile || null,
            profileImage: req.profileImage || profileImage,
            panNumber: null,
            panImage: null,
            bankName: null,
            ifsc: null,
            accountHolderName: null,
            accountNumber: null,
            bankImage: null,
            panStatus: 'Not Approved',
            bankStatus: 'Not Approved',
          });
        }

        const record = consultantMap.get(idKey);
        record.requestId = record.requestId || req._id || null;
        record.accountName = record.accountName || req.accountName || req.consultantId?.accountName || null;
        record.userName = record.userName || req.userName || req.consultantId?.userName || req.consultantId?.username || null;
        record.userId = record.userId || req.userId || req.consultantId?.userId || null;
        record.email = record.email || req.email || req.consultantId?.email || null;
        record.contactNumber = record.contactNumber || req.phone || req.mobile || req.consultantId?.phone || req.consultantId?.mobile || null;
        const newData = req.newData ? (typeof req.newData === 'string' ? JSON.parse(req.newData) : req.newData) : {};
        const currentData = req.currentData ? (typeof req.currentData === 'string' ? JSON.parse(req.currentData) : req.currentData) : {};
        const payload = { ...currentData, ...newData };

        if (req.requestType === 'BANK_EDIT' || req.requestType === 'PAN_EDIT') {
          return;
        }

        if (req.requestType === 'PAN' || req.requestType === 'PANCARD') {
          if (record.panStatus === 'Approved' && req.approveStatus !== 2) return;
          if (req.approveStatus === 2) {
            record.panNumber = payload.panNumber || record.panNumber;
            record.panImage = payload.panImage || record.panImage;
            record.panStatus = 'Approved';
          } else if (req.approveStatus === 1) {
            record.panStatus = 'Pending';
          } else {
            record.panStatus = 'Not Approved';
          }
        } else if (req.requestType === 'BANK') {
          if (record.bankStatus === 'Approved' && req.approveStatus !== 2) return;
          if (req.approveStatus === 2) {
            record.bankName = payload.bankName || record.bankName;
            record.ifsc = payload.ifsc || record.ifsc;
            record.accountHolderName = payload.bankHolderName || record.accountHolderName;
            record.accountNumber = payload.accountNumber || record.accountNumber;
            record.bankImage = payload.attachment || payload.attachmentUrl || payload.bankAttachment || record.bankImage;
            record.bankStatus = 'Approved';
          } else if (req.approveStatus === 1) {
            record.bankStatus = 'Pending';
          } else {
            record.bankStatus = 'Not Approved';
          }
        }
      });
    };

    if (liveRecords.length) {
      liveRecords.forEach((row) => {
        consultantMap.set(String(row.consultantId || row.consultantObjectId), { ...row });
      });
    } else {
      applyFromRequests(allRequests);
    }

    // Return all records (not just those with both PAN and Bank)
    let records = Array.from(consultantMap.values());

    // Apply filters for Final tab
    if (finalFilters.search) {
      const searchLower = finalFilters.search.toLowerCase().trim();
      records = records.filter(record =>
        matchesSearchValue(record.consName, searchLower) ||
        matchesSearchValue(record.accountName, searchLower) ||
        matchesSearchValue(record.userName, searchLower) ||
        matchesSearchValue(record.userId, searchLower) ||
        matchesSearchValue(record.email, searchLower) ||
        matchesSearchValue(record.contactNumber, searchLower) ||
        matchesSearchValue(record.consultantObjectId, searchLower) ||
        matchesSearchValue(record.requestId, searchLower) ||
        matchesSearchValue(record.panNumber, searchLower) ||
        matchesSearchValue(record.bankName, searchLower) ||
        matchesSearchValue(record.ifsc, searchLower) ||
        matchesSearchValue(record.accountNumber, searchLower) ||
        matchesSearchValue(record.accountHolderName, searchLower)
      );
    }

    // Explicit bank name filter - supports multiple selections
    if (finalFilters.bankName && Array.isArray(finalFilters.bankName) && finalFilters.bankName.length > 0) {
      const selectedBanks = finalFilters.bankName.filter(b => b && typeof b === 'string');
      if (selectedBanks.length > 0) {
        records = records.filter(record => {
          if (!record.bankName || typeof record.bankName !== 'string') return false;
          return selectedBanks.some(selectedBank => matchesBankName(selectedBank, record.bankName));
        });
      }
    }

    if (finalFilters.panStatus) {
      records = records.filter(record => record.panStatus === finalFilters.panStatus);
    }

    if (finalFilters.bankStatus) {
      records = records.filter(record => record.bankStatus === finalFilters.bankStatus);
    }

    if (finalFilters.imageFilter) {
      if (finalFilters.imageFilter === 'bankPassbook') {
        // Bank Passbook: Show records with bank passbook images, but exclude cancelled/check records
        records = records.filter(record => {
          const isCancelOrCheck = record.panStatus === 'Not Approved' || 
                                  record.panStatus === 'Pending' ||
                                  record.bankStatus === 'Not Approved' || 
                                  record.bankStatus === 'Pending';
          return record.bankImage && !isCancelOrCheck;
        });
      } else if (finalFilters.imageFilter === 'cancelCheck') {
        // Cancel Check Image: Show records that are cancelled/need checking, but exclude those with bank passbook images
        records = records.filter(record => {
          const isCancelOrCheck = record.panStatus === 'Not Approved' || 
                                  record.panStatus === 'Pending' ||
                                  record.bankStatus === 'Not Approved' || 
                                  record.bankStatus === 'Pending';
          return isCancelOrCheck && !record.bankImage;
        });
      }
    }

    return records;
  }, [liveRecords, allRequests, finalFilters]);

  // Bank name options for final tab (derived from final records to reflect combined view)
  const finalBankNameOptions = useMemo(() => {
    const names = new Set();
    finalRecords.forEach(rec => {
      if (rec.bankName && !excludedBankNames.includes(rec.bankName)) {
        names.add(rec.bankName);
      }
    });
    return Array.from(names);
  }, [finalRecords]);

  const finalBankNameOptionsDisplay = useMemo(() => {
    const dynamic = finalBankNameOptions.filter(name => !commonBankNames.includes(name));
    return {
      common: commonBankNames,
      dynamic,
    };
  }, [finalBankNameOptions, commonBankNames]);

  // Final records table columns
  const finalRecordsColumns = [
    {
      title: 'S. No.',
      key: 'index',
      width: 80,
      render: (_, __, index) => {
        return (finalPagination.current - 1) * finalPagination.pageSize + index + 1;
      },
    },
    {
      title: 'Profile',
      key: 'profileImage',
      width: 100,
      render: (_, record) => (
        <img
          src={record.profileImage || profileImage}
          alt={record.consName || 'Profile'}
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
      ),
    },
    {
      title: 'Consultant Name',
      dataIndex: 'consName',
      key: 'consName',
      width: 180,
      ellipsis: true,
      render: (name) => name || '---',
    },
    {
      title: 'Consultant _id',
      dataIndex: 'consultantObjectId',
      key: 'consultantObjectId',
      width: 230,
      ellipsis: true,
      render: (id) => id || '---',
    },
    {
      title: 'User ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 140,
      ellipsis: true,
      render: (id) => id || '---',
    },
    {
      title: 'PAN Number',
      dataIndex: 'panNumber',
      key: 'panNumber',
      width: 180,
      render: (pan) => pan || '---',
    },
    {
      title: 'PAN Status',
      key: 'panStatus',
      width: 140,
      render: (_, record) => {
        const status = record.panStatus || (record.panNumber ? 'Approved' : 'Not Approved');
        const color = status === 'Approved' ? 'success' : status === 'Pending' ? 'warning' : 'error';
        return (
          <Tag color={color}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'Bank Name',
      dataIndex: 'bankName',
      key: 'bankName',
      width: 220,
      render: (name) => name || '---',
    },
    {
      title: 'Bank IFSC Code',
      dataIndex: 'ifsc',
      key: 'ifsc',
      width: 180,
      render: (ifsc) => ifsc || '---',
    },
    {
      title: 'Account Holder Name',
      dataIndex: 'accountHolderName',
      key: 'accountHolderName',
      width: 200,
      render: (name) => name || '---',
    },
    {
      title: 'Account Number',
      dataIndex: 'accountNumber',
      key: 'accountNumber',
      width: 180,
      render: (account) => account || '---',
    },
    {
      title: 'Bank Status',
      key: 'bankStatus',
      width: 140,
      render: (_, record) => {
        const status = record.bankStatus || (record.bankName ? 'Approved' : 'Not Approved');
        const color = status === 'Approved' ? 'success' : status === 'Pending' ? 'warning' : 'error';
        return (
          <Tag color={color}>
            {status}
          </Tag>
        );
      },
    },
    {
      title: 'PAN Image',
      key: 'panImage',
      width: 120,
      align: 'center',
      render: (_, record) => {
        if (record.panImage) {
          return (
            <Button
              type="link"
              icon={<FileImageOutlined />}
              onClick={() => {
                setPreviewImage(record.panImage);
                setPreviewTitle(`PAN Card - ${record.consName || 'Consultant'}`);
                setImageRotation(-90);
                setImagePreviewVisible(true);
              }}
            >
              View
            </Button>
          );
        }
        return '---';
      },
    },
    {
      title: 'Bank Passbook',
      key: 'bankImage',
      width: 140,
      align: 'center',
      render: (_, record) => {
        if (record.bankImage) {
          return (
            <Button
              type="link"
              icon={<FileImageOutlined />}
              onClick={() => {
                setPreviewImage(record.bankImage);
                setPreviewTitle(`Bank Passbook - ${record.consName || 'Consultant'}`);
                setImageRotation(0);
                setImagePreviewVisible(true);
              }}
            >
              View
            </Button>
          );
        }
        return '---';
      },
    },
  ];

  return (
    <div style={{ padding: requestsOnly ? 0 : 24 }}>
      <DownloadPasswordModal />
      {requestsOnly && (
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ marginBottom: 4 }}>Bank PAN Requests</h4>
          <p style={{ color: '#666', marginBottom: 0 }}>
            Consultants asked to add or update bank and PAN details. Access requests only unlock editing; they do not change live bank/PAN records.
          </p>
        </div>
      )}
      {/* Statistics Cards */}
      {canViewRequest && <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6} md={6}>
          <Card size="small" bodyStyle={{ padding: '12px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff' }}>
                {stats.total}
              </div>
              <div style={{ color: '#666', marginTop: 4, fontSize: '12px' }}>Total Requests</div>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={6}>
          <Card size="small" bodyStyle={{ padding: '12px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 'bold', color: '#faad14' }}>
                {stats.pending}
              </div>
              <div style={{ color: '#666', marginTop: 4, fontSize: '12px' }}>Pending</div>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={6}>
          <Card size="small" bodyStyle={{ padding: '12px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 'bold', color: '#52c41a' }}>
                {stats.approved}
              </div>
              <div style={{ color: '#666', marginTop: 4, fontSize: '12px' }}>Approved</div>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6} md={6}>
          <Card size="small" bodyStyle={{ padding: '12px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 'bold', color: '#ff4d4f' }}>
                {stats.notApproved}
              </div>
              <div style={{ color: '#666', marginTop: 4, fontSize: '12px' }}>Rejected</div>
            </div>
          </Card>
        </Col>
      </Row>}

      {/* Tabs */}
      <Card style={{ marginBottom: 16 }}>
        {!requestsOnly && (
        <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #eee', marginBottom: 16 }}>
          {canViewRequest && <button
            onClick={() => setActiveTab('request')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'request' ? '#ff8989' : 'transparent',
              color: activeTab === 'request' ? 'white' : '#666',
              cursor: 'pointer',
              borderRadius: '4px 4px 0 0',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
          >
            Request
          </button>}
          {canViewRecord && <button
            onClick={() => setActiveTab('final')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'final' ? '#ff8989' : 'transparent',
              color: activeTab === 'final' ? 'white' : '#666',
              cursor: 'pointer',
              borderRadius: '4px 4px 0 0',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
          >
            Record
          </button>}
        </div>
        )}

        {/* Filters based on active tab */}
        {activeTab === 'request' && canViewRequest ? (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'space-between', overflowX: 'auto' }}>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'nowrap', alignItems: 'center', minWidth: 0 }}>
              <Input
              placeholder="Search by consultant/account/user name, ID, PAN, IFSC, holder, or account no."
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                style={{ width: 250, minWidth: 200 }}
                allowClear
              />

            <Select
              mode="multiple"
              style={{ width: 220, minWidth: 180 }}
              value={Array.isArray(filters.bankName) && filters.bankName.length > 0 ? filters.bankName : []}
              onChange={(value) => handleFilterChange('bankName', Array.isArray(value) ? value : [])}
              placeholder="Filter by bank name (e.g., SBI, HDFC, ICICI)"
              allowClear
              showSearch
              optionFilterProp="children"
              maxTagCount="responsive"
            >
              {bankNameOptionsDisplay.common.map(name => (
                <Option key={`common-${name}`} value={name}>{name}</Option>
              ))}
              {bankNameOptionsDisplay.dynamic.map(name => (
                <Option key={name} value={name}>{name}</Option>
              ))}
            </Select>

              <Select
                style={{ width: 150, minWidth: 130 }}
                value={filters.requestType}
                onChange={(value) => handleFilterChange('requestType', value)}
                placeholder="Request Type"
                allowClear
              >
                <Option value="">All Types</Option>
                <Option value="BANK">Bank (details + edit access)</Option>
                <Option value="BANK_EDIT">Bank edit access</Option>
                <Option value="PAN">PAN (card + edit access)</Option>
                <Option value="PAN_EDIT">PAN edit access</Option>
              </Select>

              <Select
                style={{ width: 150, minWidth: 130 }}
                value={filters.approveStatus}
                onChange={(value) => handleFilterChange('approveStatus', value)}
                placeholder="Status"
                allowClear
              >
                <Option value="">All Status</Option>
                <Option value="1">Pending</Option>
                <Option value="2">Approved</Option>
                <Option value="3">Rejected</Option>
              </Select>

              {/* <Select
                style={{ width: 180, minWidth: 160 }}
                value={filters.imageFilter}
                onChange={(value) => handleFilterChange('imageFilter', value)}
                placeholder="Image Filter"
                allowClear
              >
                <Option value="">All Images</Option>
                <Option value="bankPassbook">Bank Passbook</Option>
                <Option value="cancelCheck">Cancel Check Image</Option>
              </Select> */}
            </div>

            <Space style={{ flexShrink: 0 }}>
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={() => requestDownloadWithCallback(exportToExcel, 'Bank/PAN Management')}
                style={{ backgroundColor: '#b7e8c2', borderColor: '#b7e8c2', color: '#167c2c' }}
              >
                Export Excel
              </Button>
              <Button
                type="primary"
                danger
                icon={<FilePdfOutlined />}
                onClick={exportToPDF}
                style={{ backgroundColor: '#ffd6d6', borderColor: '#ffd6d6', color: '#a8071a' }}
              >
                Export PDF
              </Button>
            </Space>
          </div>
        ) : canViewRecord ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div style={{ display: 'flex', gap: 16, flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'space-between', overflowX: 'auto' }}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'nowrap', alignItems: 'center', minWidth: 0 }}>
                <Input
                  placeholder="Search by consultant/account/user name, ID, PAN, IFSC, bank holder, or account no."
                  prefix={<SearchOutlined />}
                  value={finalFilters.search}
                  onChange={(e) => handleFinalFilterChange('search', e.target.value)}
                  style={{ width: 250, minWidth: 200 }}
                  allowClear
                />

                <Select
                  mode="multiple"
                  style={{ width: 220, minWidth: 180 }}
                  value={finalFilters.bankName && finalFilters.bankName.length > 0 ? finalFilters.bankName : undefined}
                  onChange={(value) => handleFinalFilterChange('bankName', value || [])}
                  placeholder="Filter by bank name (e.g., SBI, HDFC, ICICI)"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  maxTagCount="responsive"
                >
                  {finalBankNameOptionsDisplay.common.map(name => (
                    <Option key={`final-common-${name}`} value={name}>{name}</Option>
                  ))}
                  {finalBankNameOptionsDisplay.dynamic.map(name => (
                    <Option key={`final-${name}`} value={name}>{name}</Option>
                  ))}
                </Select>

                <Select
                  style={{ width: 150, minWidth: 130 }}
                  value={finalFilters.panStatus}
                  onChange={(value) => handleFinalFilterChange('panStatus', value)}
                  placeholder="PAN Status"
                  allowClear
                >
                  <Option value="">All PAN Status</Option>
                  <Option value="Approved">Approved</Option>
                  <Option value="Pending">Pending</Option>
                  <Option value="Not Approved">Not Approved</Option>
                </Select>

                <Select
                  style={{ width: 150, minWidth: 130 }}
                  value={finalFilters.bankStatus}
                  onChange={(value) => handleFinalFilterChange('bankStatus', value)}
                  placeholder="Bank Status"
                  allowClear
                >
                  <Option value="">All Bank Status</Option>
                  <Option value="Approved">Approved</Option>
                  <Option value="Pending">Pending</Option>
                  <Option value="Not Approved">Not Approved</Option>
                </Select>

                {/* <Select
                  style={{ width: 180, minWidth: 160 }}
                  value={finalFilters.imageFilter}
                  onChange={(value) => handleFinalFilterChange('imageFilter', value)}
                  placeholder="Image Filter"
                  allowClear
                >
                  <Option value="">All Images</Option>
                  <Option value="bankPassbook">Bank Passbook</Option>
                  <Option value="cancelCheck">Cancel Check Image</Option>
                </Select> */}
              </div>

              <Space style={{ flexShrink: 0 }}>
                <Button
                  type="primary"
                  icon={<FileExcelOutlined />}
                onClick={() => requestDownloadWithCallback(exportFinalToExcel, 'Bank/PAN Management (Final)')}
                style={{ backgroundColor: '#b7e8c2', borderColor: '#b7e8c2', color: '#167c2c' }}
              >
                Export Excel
                </Button>
                <Button
                  type="primary"
                  danger
                  icon={<FilePdfOutlined />}
                  onClick={exportFinalToPDF}
                  style={{ backgroundColor: '#ffd6d6', borderColor: '#ffd6d6', color: '#a8071a' }}
                >
                  Export PDF
                </Button>
              </Space>
            </div>
          </Space>
        ) : null}
      </Card>

      {/* Content based on active tab */}
      {activeTab === 'request' && canViewRequest ? (
        /* Main Requests Table */
        <Card>
          <Table
            columns={columns}
            dataSource={filteredRequestList}
            rowKey="_id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: filteredAllRequests.length,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} requests`,
              pageSizeOptions: ['10', '20', '50', '100'],
              showPrevNextJumpers: true,
              showQuickJumper: true,
            }}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
          />
        </Card>
      ) : canViewRecord ? (
        /* Final Records Section - Combined Bank & PAN Details */
        <Card>
          <p style={{ marginBottom: 16, color: '#666', fontSize: '14px' }}>
            This section shows all consultants with PAN and/or Bank details, including approved, pending, and not approved statuses. This is a read-only view.
          </p>
          <Table
            columns={finalRecordsColumns}
            dataSource={finalRecords}
            rowKey="consultantId"
            pagination={{
              current: finalPagination.current,
              pageSize: finalPagination.pageSize,
              total: finalRecords.length,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} consultants`,
              pageSizeOptions: ['10', '20', '50', '100'],
              showPrevNextJumpers: true,
              showQuickJumper: true,
            }}
            onChange={(paginationInfo) => {
              setFinalPagination({
                current: paginationInfo.current,
                pageSize: paginationInfo.pageSize,
              });
            }}
            scroll={{ x: 1800 }}
          />
        </Card>
      ) : null}

      {/* Request Detail Modal */}
      {canViewRequest && viewModal && (
        <RequestDetail
          viewModal={viewModal}
          setViewModal={setViewModal}
          requestId={requestId}
          getRequestList={refreshBankPanViews}
          requestDetail={requestDetail}
          newData={newData}
          currentData={currentData}
          requestdetails={requestdetails}
          requestType={filters.requestType || ''}
        />
      )}

      {/* Image Preview Modal */}
      <Modal
        title={previewTitle}
        open={imagePreviewVisible}
        onCancel={() => {
          setImagePreviewVisible(false);
          setImageRotation(0);
        }}
        footer={[
          <Button key="rotate" onClick={() => setImageRotation(prev => prev - 90)}>
            Rotate
          </Button>,
          <Button key="close" type="primary" onClick={() => {
            setImagePreviewVisible(false);
            setImageRotation(0);
          }}>
            Close
          </Button>
        ]}
        width={900}
        centered
        style={{ top: 20 }}
        bodyStyle={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '400px',
          maxHeight: '75vh',
          overflow: 'auto',
          padding: '20px'
        }}
      >
        {previewImage && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            width: '100%',
            height: '100%'
          }}>
            <img
              src={previewImage}
              alt={previewTitle}
              style={{ 
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                transform: `rotate(${imageRotation}deg)`,
                transformOrigin: 'center center',
                transition: 'transform 0.3s ease'
              }}
              onClick={() => {
                // Open in new window for full view
                window.open(previewImage, '_blank');
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                toast.error('Failed to load image');
              }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BankPanManagement;

